import i18next, { t } from "i18next"
import { Card, Col, Form, Nav, Row } from "react-bootstrap"
import AsyncSelect from "react-select/async"
import MotornetVehicleSelector, { MotornetSelectedVehicle, getMakeName } from "src/motornet/common/MotornetVehicleSelector"
import { productionFetchParams } from "src/motornet/common/selectorMeta"
import SelectOptWithSubtitle from "src/components/SelectOptWithSubtitle/SelectOptWithSubtitle"
import TiassistoElement from "src/components/TiAssisto/TiassistoElement"
import VehicleCardBody from "src/components/VehicleCardBody/VehicleCardBody"
import OpportunityFunnelDriveItEasyForm from "../../OpportunityFunnelDriveItEasyForm/OpportunityFunnelDriveItEasyForm"
import { useState } from "react"
import { useApiClient } from "src/hooks/useApiClient"
import { VehicleEntity } from "src/api"

export function SelectVehiclePanel({
  funnelData,
  setFunnelData,
  setVehicleSearchOpt,
  vehicleSearchOpt,
  vehicleSearch,
  selectedOpportunityStage,
  opportunityValues,
  setOpportunityValues,
  activateDriveItEasy,
  setActivateDriveItEasy
}) {
  const { apiClient, handleApiResponse } = useApiClient();
  const [selectedVehicle, setSelectedVehicle] = useState<MotornetSelectedVehicle>({
    brand: null,
    model: null,
    version: null,
  })

  const modelLabel = (model: any) => model?.data?.gammaModello?.descrizione || model?.label || '';

  // Motornet images live on the full vehicle, keyed by the version's eurotax code.
  const fetchImages = async (version: any, brand: any, model: any) => {
    const eurotax = version?.data?.codiceEurotax;
    let imgs: string[] = [];
    if (eurotax) {
      try {
        const vehicleKind = version?.data?.vehicleKind ?? brand?.data?.vehicleKind ?? 'auto';
        const payload = await apiClient.motornetControllerService.getMotornetVehicle(eurotax, vehicleKind, productionFetchParams(version?.data, model?.data));
        imgs = (payload?.images ?? []).map((img: any) => img?.url).filter(Boolean);
      } catch (e) {
        console.warn('Failed to fetch motornet vehicle images', e);
      }
    }
    setFunnelData((prev) => ({
      ...prev,
      motornetSelectedVehicle: { brand, model, version, images: imgs }
    }))
  }

  const setVehicleSearch = (opt: any) => {
    setVehicleSearchOpt(opt)

    setFunnelData({ ...funnelData, vehicle: undefined })

    // FETCH THE VEHICLE SELECTED FROM THE DROPDOWN
    handleApiResponse(apiClient.vehicleController.getVehicles1(opt.vehicle.id))
      .then((res: VehicleEntity) => {
        setFunnelData({ ...funnelData, vehicle: res })
        // Update OpportunityValues targa, telaio and dataFineContrattoDriveItEasy because they are the only values OpportunityValues that are saved into the hmp vehicle.
        // They are needed to autofill OpportunityFunnelDriveItEasyForm component
        setOpportunityValues(prev => ({
          ...prev,
          targa: res.dettagli_veicolo?.targa || prev.targa,
          telaio: res.dettagli_veicolo?.telaio || prev.telaio,
          dataFineContrattoDriveItEasy: res.stato_corrente?.dataFineContrattoDriveItEasy || prev.dataFineContrattoDriveItEasy,
        }));
      })
      .catch(error => {
        console.error('Error fetching vehicle details: ', error);
      });
  }


  return (
    <div>
      <div>
        <Row className='mb-3'>
          <Nav variant='tabs' activeKey={funnelData.vehicleSource} onSelect={(k) => {
            setFunnelData({
              ...funnelData,
              vehicle: undefined,
              motornetSelectedVehicle: { model: null, brand: null, version: null, images: [] },
              vehicleSource: k || 'hmp'
            })
            setSelectedVehicle({ brand: null, model: null, version: null })
            setVehicleSearchOpt(null)
          }}>
            <Nav.Item>
              <Nav.Link eventKey="hmp">	{t('vehicle_hmp')}</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="external">	{t('vehicle_external')}</Nav.Link>
            </Nav.Item>
          </Nav>
        </Row>

        {/* SELECT VEHICLE */}
        <Row>
          {funnelData.vehicleSource === 'hmp' && (
            <Col md={6}>
              <Form.Group>
                <AsyncSelect
                  menuPosition="fixed"
                  placeholder={i18next.t('select')}
                  value={vehicleSearchOpt}
                  noOptionsMessage={() => {
                    return i18next.t('vehicle_search_placeholder', { defaultValue: 'Ordine, telaio, targa o codice costruttore' })
                  }}
                  defaultOptions={vehicleSearchOpt ? [vehicleSearchOpt] : []}
                  loadOptions={vehicleSearch}
                  onChange={setVehicleSearch}
                  components={{
                    Option: (props: any) => {
                      return <SelectOptWithSubtitle {...props}></SelectOptWithSubtitle>
                    }
                  }}
                />
              </Form.Group>
            </Col>
          )}

          {funnelData.vehicleSource === 'external' && (
            <MotornetVehicleSelector
              selectedVehicle={selectedVehicle}
              onBrandChange={(brand) => {
                setSelectedVehicle({ brand, model: null, version: null })
                setFunnelData({
                  ...funnelData,
                  vehicle: undefined,
                  motornetSelectedVehicle: { brand, model: null, version: null, images: [] },
                })
              }}
              onModelChange={(model) => {
                setSelectedVehicle((prev) => ({ ...prev, model, version: null }))
                setFunnelData((prev) => ({
                  ...prev,
                  motornetSelectedVehicle: { brand: selectedVehicle.brand, model, version: null, images: [] },
                }))
              }}
              onVersionChange={(version) => {
                setSelectedVehicle((prev) => ({ ...prev, version }))
                fetchImages(version, selectedVehicle.brand, selectedVehicle.model)
              }}
            />
          )}
        </Row>
      </div>

      <Card className='mt-4'>
        {funnelData.vehicle ? (
          <VehicleCardBody data={funnelData.vehicle} />
        ) : (funnelData.motornetSelectedVehicle?.images?.length > 0 ?
          <VehicleCardBody data={{
            dati_veicolo: {
              marca: getMakeName(funnelData.motornetSelectedVehicle.brand),
              modello: modelLabel(funnelData.motornetSelectedVehicle.model)
            },
            image_list: funnelData.motornetSelectedVehicle.images.map(el => ({ url: el }))
          }} />
          : null)}
      </Card>

      <TiassistoElement>
        <OpportunityFunnelDriveItEasyForm {
          ...{
            finalState: selectedOpportunityStage,
            opportunityValues,
            setOpportunityValues,
            activateDriveItEasy,
            setActivateDriveItEasy
          }} />
      </TiassistoElement>
    </div>
  )
}
